<template>
    <div class="mx-auto w-full">
        <!-- <dl
                class="mt-5 grid grid-cols-1 divide-y divide-border overflow-hidden rounded-lg bg-card shadow-sm lg:grid-cols-1 lg:divide-x lg:divide-y-0">
                <div class="p-4 px-6">
                    <div class="flex justify-between items-center">
                        <div>
                            <dt class="text-base font-normal text-muted-foreground">Usage this billing cycle</dt>
                            <dd class="mt-1 flex items-baseline gap-2 md:block lg:flex">
                                <div class="flex items-baseline text-2xl font-medium text-chart-1">
                                    $430
                                    <span class="ml-2 text-sm font-medium text-muted-foreground">( previous month $365.5 )</span>
                                </div>

                                <Badge variant="default" class="md:mt-2 lg:mt-0">
                                    <ArrowUpIcon class="mr-0.5 shrink-0 self-center h-4 w-4 text-chart-1" />
                                    <span class="sr-only"> Increased by </span>
                                    12%
                                </Badge>
                            </dd>
                        </div>
                        <Button variant="default" size="sm" class="bg-chart-1 hover:bg-primary">
                            Billing
                        </Button>
                    </div>
                </div>
                <div class="p-4 px-6">
                    <div class="flex justify-between items-center">
                        <div>
                            <dt class="text-base font-normal text-muted-foreground">Services</dt>
                            <dd class="mt-1 flex items-baseline gap-2 md:block lg:flex">
                                <div class="flex items-baseline text-2xl font-medium text-chart-1">
                                    2
                                    <span class="ml-2 text-sm font-medium text-chart-1">( 2/3 running )</span>
                                </div>
                            </dd>
                        </div>
                        <Button variant="default" size="sm" class="bg-chart-1 hover:bg-primary">
                            Manage
                        </Button>
                    </div>
                </div>
            </dl> -->
        <div class="mx-auto w-full">
            <div class="mb-5">
                <div class="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        v-for="stat in generalStats"
                        :key="stat.id"
                        :title="stat.name"
                        :value="stat.value"
                        :icon="stat.icon"
                        :info-icon="false"
                        :info-items="stat.info || []"
                        :loading="orgStatisticsLoading" />
                </div>
            </div>
            <div class="mt-10 mb-2">
                <div class="flex justify-between items-end mb-3">
                    <h3 class="font-semibold">Dashboard Metrics</h3>
                </div>

                <Card class="bg-sidebar-primary-foreground">
                    <CardHeader>
                        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                                <TimeRangeFilter
                                    label="Time Range"
                                    :model-value="convertFilterKeyToTimeRangeValue(selectedUsageTimeseriesDateFilter?.key || 'last24h')"
                                    :disabled="timeseriesLoading || globalSourceConfigsLoading || globalDestinationConfigsLoading"
                                    :include-live-option="false"
                                    :include-all-time-option="false"
                                    :include-date-options="false"
                                    :include-short-ranges="false"
                                    :hide-options="[]"
                                    @update:model-value="onTimeRangeChange" />
                                <SelectDropdown
                                    label="Time Grain"
                                    :options="timeUnitOptions"
                                    :model-value="selectedTimeUnit"
                                    :disabled="timeseriesLoading || globalSourceConfigsLoading || globalDestinationConfigsLoading"
                                    @update:model-value="onTimeUnitChange" />
                            </div>
                            <div class="flex flex-col min-w-0 sm:max-w-xs">
                                <div class="text-xs text-muted-foreground">Metrics</div>
                                <MultiSelect
                                    v-model="selectedMetricKeys"
                                    :options="listOfUsageMetrics"
                                    :placeholder="
                                        timeseriesLoading || globalSourceConfigsLoading || globalDestinationConfigsLoading
                                            ? 'Loading...'
                                            : listOfUsageMetrics?.length
                                              ? 'Select metrics'
                                              : 'No metrics available'
                                    "
                                    :disabled="
                                        timeseriesLoading ||
                                        globalSourceConfigsLoading ||
                                        globalDestinationConfigsLoading ||
                                        !listOfUsageMetrics?.length
                                    "
                                    :option-label="'name'"
                                    :track-by="'key'"
                                    :enable-refresh="true"
                                    :refresh-loading="timeseriesLoading"
                                    :show-tags="false"
                                    @refresh="() => fetchUsageTimeseries()" />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent class="pt-0">
                        <HighChart
                            :chart-options="chartOptions"
                            :loading="timeseriesLoading || globalSourceConfigsLoading || globalDestinationConfigsLoading" />
                    </CardContent>
                </Card>
            </div>
        </div>
    </div>
</template>

<script setup>
import _ from 'lodash';
import { computed, ref, onMounted, watch } from 'vue';
import { useUrlFilters } from '@/composables/useUrlFilters';
import { FILTER_DEFAULTS, TIME_RANGE_LABELS } from '@/utils/constants';
import { getTimeRangeLabel, createDateFilterFromTimeRange, convertFilterKeyToTimeRangeValue } from '@/utils/utils';

import { ArrowUpDown, Share2, ListChecks, SlidersHorizontal } from 'lucide-vue-next';
import StatCard from '@/components/global/StatCard.vue';
import TimeRangeFilter from '@/components/filters/TimeRangeFilter.vue';
import SelectDropdown from '@/components/filters/SelectDropdown.vue';
import HighChart from '../../components/HighChart.vue';
import { formatChartXAxisLabels, formatter, getMetricFormattingInfo } from '../../utils/utils';
import { useMetricsStore } from '../../stores/metrics.store';
import moment from 'moment-timezone';
import { MultiSelect } from '@/components/ui/multi-select';
import { Card, CardHeader, CardContent } from '@/components/ui/card';

const metricsStore = useMetricsStore();

// Set up URL filters
const { registerFilter } = useUrlFilters({
    defaults: {
        selectedUsageTimeseriesDateFilter: { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H },
        selectedUsageTimeseriesTimeUnit: FILTER_DEFAULTS.TIME_UNITS.HOUR,
        allSelectedMetricsForUsageChart: []
    },
    serializers: {
        selectedUsageTimeseriesDateFilter: (value) => JSON.stringify(value),
        selectedUsageTimeseriesTimeUnit: (value) => String(value),
        allSelectedMetricsForUsageChart: (value) => JSON.stringify(value)
    },
    deserializers: {
        selectedUsageTimeseriesDateFilter: (value) => {
            try {
                return JSON.parse(value);
            } catch {
                return { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
            }
        },
        selectedUsageTimeseriesTimeUnit: (value) => value || FILTER_DEFAULTS.TIME_UNITS.HOUR,
        allSelectedMetricsForUsageChart: (value) => {
            try {
                return value ? JSON.parse(value) : [];
            } catch {
                return [];
            }
        }
    }
});

// Register filters and get reactive references
const selectedUsageTimeseriesDateFilter = registerFilter('selectedUsageTimeseriesDateFilter');
const selectedTimeUnit = registerFilter('selectedUsageTimeseriesTimeUnit');
const allSelectedMetricsForUsageChart = registerFilter('allSelectedMetricsForUsageChart');

const orgStatisticsLoading = ref(false);
const timeseriesLoading = ref(false);
const globalSourceConfigsLoading = ref(false);
const globalDestinationConfigsLoading = ref(false);
const isInitializing = ref(true);

const orgStats = ref({});

// Computed property to bridge object-based storage with key-based MultiSelect v-model
const selectedMetricKeys = computed({
    get() {
        return allSelectedMetricsForUsageChart.value.map((metric) => metric.key);
    },
    set(keys) {
        // Convert keys back to full objects by finding them in listOfUsageMetrics
        allSelectedMetricsForUsageChart.value = keys.map((key) => listOfUsageMetrics.value.find((metric) => metric.key === key)).filter(Boolean); // filter out any undefined values
    }
});

const usageTimeseriesData = ref([]);

const timeUnitOptions = [
    { name: 'Month', value: 'month' },
    { name: 'Day', value: 'day' },
    { name: 'Hour', value: 'hour' },
    { name: 'Minute', value: 'minute' }
];

onMounted(async () => {
    // Load org statistics independently (fastest, shows immediately)
    orgStatisticsLoading.value = true;
    metricsStore.organization
        .fetchOrgStatistics()
        .then((data) => {
            orgStats.value = data;
        })
        .catch((error) => {
            console.error('Error loading org statistics:', error);
        })
        .finally(() => {
            orgStatisticsLoading.value = false;
        });

    // Load chart dependencies and timeseries data in parallel
    globalSourceConfigsLoading.value = true;
    globalDestinationConfigsLoading.value = true;

    // Start all chart-related loading in parallel
    Promise.all([
        metricsStore.sources.fetchGlobalSourceConfigs(),
        metricsStore.destinations.fetchGlobalDestinationConfigs(),
        fetchUsageTimeseries(selectedUsageTimeseriesDateFilter.value, selectedTimeUnit.value)
    ])
        .catch((error) => {
            console.error('Error loading chart data:', error);
            // Ensure timeseries loading is stopped on error
            timeseriesLoading.value = false;
        })
        .finally(() => {
            globalSourceConfigsLoading.value = false;
            globalDestinationConfigsLoading.value = false;
            isInitializing.value = false;
        });
});

const listOfUsageMetrics = computed(() => {
    let sourceMetricsKeys = Object.keys(usageTimeseriesData.value?.sources?.account?.usage || {});
    let destinationMetricsKeys = Object.keys(usageTimeseriesData.value?.destinations?.account?.usage || {});

    sourceMetricsKeys = sourceMetricsKeys
        .map((key) => {
            const metricInfo = getMetricFormattingInfo(key, metricsStore.sources.globalConfigs?.metrics);

            if (!metricInfo) {
                return null;
            }

            return {
                ...metricInfo,
                key,
                type: 'source',
                name: `Sources - ${metricInfo.name}`
            };
        })
        .filter((x) => x && usageTimeseriesData.value?.sources?.account?.usage?.[x.key]?.length);

    destinationMetricsKeys = destinationMetricsKeys
        .map((key) => {
            const metricInfo = getMetricFormattingInfo(key, metricsStore.destinations.globalConfigs?.metrics); // TODO: Change to destinations

            if (!metricInfo) {
                return null;
            }

            return {
                ...metricInfo,
                key,
                type: 'destination',
                name: `Destinations - ${metricInfo.name}`
            };
        })
        .filter((x) => x && usageTimeseriesData.value?.destinations?.account?.usage?.[x.key]?.length);

    const keysToReturn = [...sourceMetricsKeys, ...destinationMetricsKeys];

    // Don't clear metrics selection on API failure to avoid infinite loops
    // Just return available metrics - the watcher will handle validation

    return _.orderBy(keysToReturn, 'name', 'asc');
});

// Separate computed for just tracking metric keys (for watcher optimization)
const availableUsageMetricKeys = computed(() => {
    return listOfUsageMetrics.value.map((m) => m.key).sort();
});

watch(
    () => availableUsageMetricKeys.value,
    (newKeys, oldKeys) => {
        // Avoid infinite loops by checking if the keys actually changed
        if (JSON.stringify(newKeys) === JSON.stringify(oldKeys)) {
            return;
        }

        const defaultTo = ['bytesConsumedTotal', 'byteTotal'];
        const availableMetrics = listOfUsageMetrics.value; // Get full metrics objects

        // Validate and clear invalid metrics to prevent crashes
        if (!newKeys?.length) {
            // No metrics available - clear selection
            if (allSelectedMetricsForUsageChart.value.length) {
                allSelectedMetricsForUsageChart.value = [];
            }
            return;
        }

        // Set default metrics if none are selected (including during initialization)
        if (!allSelectedMetricsForUsageChart.value.length && availableMetrics.length) {
            const defaultMetrics = availableMetrics.filter((x) => defaultTo.includes(x.key));
            allSelectedMetricsForUsageChart.value = defaultMetrics.length ? defaultMetrics : [availableMetrics[0]];
        }

        // If we have selected metrics and not initializing, validate they still exist in available metrics
        else if (!isInitializing.value && allSelectedMetricsForUsageChart.value.length && availableMetrics.length) {
            const validMetrics = allSelectedMetricsForUsageChart.value.filter((selected) =>
                availableMetrics.find((available) => available.key === selected.key)
            );

            // Only update if some metrics became invalid
            if (validMetrics.length !== allSelectedMetricsForUsageChart.value.length) {
                allSelectedMetricsForUsageChart.value = validMetrics;
            }
        }
    },
    { flush: 'post' } // Run after DOM updates to avoid timing issues
);

// Metrics selection should NOT trigger API calls - it should only affect chart display from existing data
// Removed the watcher that was causing infinite loops on API failures

// Watch for date filter changes - only affects chart data
watch(
    () => selectedUsageTimeseriesDateFilter.value,
    async (newValue) => {
        if (!isInitializing.value && newValue) {
            timeseriesLoading.value = true;
            await fetchUsageTimeseries(newValue, selectedTimeUnit.value);
        }
    },
    { deep: true }
);

// Watch for time unit changes - only affects chart data
watch(
    () => selectedTimeUnit.value,
    async (newValue) => {
        if (!isInitializing.value && newValue) {
            timeseriesLoading.value = true;
            await fetchUsageTimeseries(selectedUsageTimeseriesDateFilter.value, newValue);
        }
    }
);

const generalStats = computed(() => {
    const goodStatuses = ['Active', 'Paused', 'Starting', 'Stopped'];
    const badStatuses = ['Broken', 'Unknown'];
    const allStatuses = [...goodStatuses, ...badStatuses];

    return [
        {
            id: 2,
            name: 'Pipelines',
            value: _.sum(allStatuses.map((status) => orgStats.value?.pipelines?.[status] || 0)),
            icon: ArrowUpDown,
            backgroundColorClass: 'bg-primary',
            info: [
                {
                    value: _.sum(badStatuses.map((status) => orgStats.value?.pipelines?.[status] || 0)),
                    label: 'broken',
                    colorClass: 'bg-destructive/10 text-destructive ring-destructive/20'
                },
                {
                    value: _.sum(goodStatuses.map((status) => orgStats.value?.pipelines?.[status] || 0)),
                    label: 'active',
                    colorClass: 'bg-chart-1/10 text-chart-1 ring-chart-1/20'
                }
            ]
        },
        {
            id: 0,
            name: 'Connectors',
            value:
                _.sum(allStatuses.map((status) => orgStats.value?.destinations?.[status] || 0)) +
                _.sum(allStatuses.map((status) => orgStats.value?.sources?.[status] || 0)),
            icon: Share2,
            backgroundColorClass: 'bg-primary',
            info: [
                {
                    value:
                        _.sum(badStatuses.map((status) => orgStats.value?.destinations?.[status] || 0)) +
                        _.sum(badStatuses.map((status) => orgStats.value?.sources?.[status] || 0)),
                    label: 'broken',
                    colorClass: 'bg-destructive/10 text-destructive ring-destructive/20'
                },
                {
                    value:
                        _.sum(goodStatuses.map((status) => orgStats.value?.destinations?.[status] || 0)) +
                        _.sum(goodStatuses.map((status) => orgStats.value?.sources?.[status] || 0)),
                    label: 'active',
                    colorClass: 'bg-chart-1/10 text-chart-1 ring-chart-1/20'
                }
            ]
        },
        {
            id: 3,
            name: 'Topics',
            value: orgStats.value?.topics || 0,
            icon: ListChecks,
            backgroundColorClass: 'bg-primary'
        },
        {
            id: 4,
            name: 'Transforms',
            value: orgStats.value?.transforms || 0,
            icon: SlidersHorizontal,
            backgroundColorClass: 'bg-primary'
        }
    ];
});

const fetchUsageTimeseries = async (specificDateFilter, specificTimeUnit) => {
    timeseriesLoading.value = true;

    // Update filter values if provided
    if (specificDateFilter !== undefined) {
        selectedUsageTimeseriesDateFilter.value = specificDateFilter;
    }
    if (specificTimeUnit !== undefined) {
        selectedTimeUnit.value = specificTimeUnit;
    }

    const currentDateFilter = selectedUsageTimeseriesDateFilter.value;
    const currentTimeUnit = selectedTimeUnit.value;

    const isDefaultDateFilter = currentDateFilter?.key === 'last24h' || !currentDateFilter?.key;
    const defaultDateFilter = isDefaultDateFilter;

    try {
        const params = defaultDateFilter
            ? { timeUnit: currentTimeUnit }
            : {
                  start: currentDateFilter?.value?.start,
                  end: currentDateFilter?.value?.end,
                  timeUnit: currentTimeUnit
              };
        const timeseriesData = await metricsStore.organization.fetchUsageTimeseriesMetricsForAllConnectors(params);

        usageTimeseriesData.value = timeseriesData;

        return timeseriesData;
    } catch (err) {
        console.error('Dashboard error:', err);
    } finally {
        timeseriesLoading.value = false;
    }
};

const chartOptions = computed(() => {
    const titleText = getTimeRangeLabel(selectedUsageTimeseriesDateFilter.value?.key) || TIME_RANGE_LABELS.LAST_24H;

    const metricKeys =
        _.cloneDeep(allSelectedMetricsForUsageChart.value)
            ?.map((x) => (x.key ? { key: x.key, type: x.type, name: x.name } : null))
            .filter((x) => x) || [];

    const dataForEachSelectedMetric = metricKeys
        .map(({ key, type, name }) => {
            const sourcesMetricData = usageTimeseriesData.value?.sources?.account?.usage[key];
            const destinationsMetricData = usageTimeseriesData.value?.destinations?.account?.usage[key];

            if (sourcesMetricData?.length && destinationsMetricData?.length) {
                return [
                    {
                        metricKey: key,
                        metricData: sourcesMetricData,
                        metricInfo: {
                            ...getMetricFormattingInfo(
                                key,
                                type === 'source' ? metricsStore.sources.globalConfigs?.metrics : metricsStore.destinations.globalConfigs?.metrics
                            ),
                            name
                        }
                    },
                    {
                        metricKey: key,
                        metricData: destinationsMetricData,
                        metricInfo: {
                            ...getMetricFormattingInfo(
                                key,
                                type === 'source' ? metricsStore.sources.globalConfigs?.metrics : metricsStore.destinations.globalConfigs?.metrics
                            ),
                            name
                        }
                    }
                ];
            }

            return {
                metricKey: key,
                metricData: sourcesMetricData || destinationsMetricData,
                metricInfo: {
                    ...getMetricFormattingInfo(
                        key,
                        type === 'source' ? metricsStore.sources.globalConfigs?.metrics : metricsStore.destinations.globalConfigs?.metrics
                    ),
                    name
                }
            };
        })
        .flat();

    const seriesAndYAxes = dataForEachSelectedMetric.map((metric, index) => {
        const yAxis = {
            title: {
                text: metric.metricInfo?.name
            },
            labels: {
                formatter: function () {
                    return formatter(this.value, { unit: metric.metricInfo?.unit }, false).display;
                }
            },
            opposite: index % 2 !== 0
        };

        const series = {
            name: metric.metricInfo?.name,
            data: metric.metricData?.map((dataItem) => {
                const localTime = moment.utc(dataItem.timestamp).tz(moment.tz.guess()).toDate();
                return {
                    x: localTime,
                    y: dataItem.value
                };
            }),
            type: 'line',
            yAxis: index,
            custom: {
                unit: metric.metricInfo?.unit
            },
            dataLabels: {
                enabled: true,
                formatter: function () {
                    return formatter(this.y, { unit: metric.metricInfo?.unit }, false).display;
                }
            }
        };

        return { series, yAxis };
    });

    // Split the series and yAxis configurations
    const series = seriesAndYAxes.map((sy) => sy.series);
    const yAxis = seriesAndYAxes.map((sy) => sy.yAxis);

    const options = {
        time: {
            timezone: moment.tz.guess()
        },
        chart: {
            zoomType: 'xy'
        },
        title: {
            text: titleText
        },
        subtitle: {
            text:
                selectedTimeUnit.value === 'hour'
                    ? 'Hourly Data'
                    : selectedTimeUnit.value === 'minute'
                      ? 'Minute Data'
                      : selectedTimeUnit.value === 'month'
                        ? 'Monthly Data'
                        : 'Daily Data'
        },
        caption: {
            text: `Timezone: ${moment.tz.guess()}`
        },
        xAxis: {
            type: 'datetime',
            labels: {
                formatter: formatChartXAxisLabels
            }
        },
        yAxis: yAxis,
        series: series,
        tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
                const localTime = moment(new Date(this.x)).tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');

                let tooltip = `${localTime}<br>`;
                this.points.forEach((point) => {
                    tooltip += `<b>${point.series.name}:</b> ${
                        formatter(point.y, { unit: point.series.userOptions.custom?.unit }, false).display
                    }<br>`;
                });
                return tooltip;
            }
        }
    };

    return options;
});

async function onTimeRangeChange(timeRangeValue) {
    const dateFilter = createDateFilterFromTimeRange(timeRangeValue);
    selectedUsageTimeseriesDateFilter.value = dateFilter;
    selectedTimeUnit.value = dateFilter.timeUnit;
}

async function onTimeUnitChange(value) {
    selectedTimeUnit.value = value;
}
</script>

<style>
/* Custom CSS for aspect ratio */
.aspect-square {
    position: relative;
}
</style>
